
import SearchResults from '@/components/Search/SearchResults.vue'
import SearchBar from '@/components/Search/SearchBar.vue'
import SearchFilters from '@/components/Search/Filtering/SearchFilters.vue'
import Suggestions from '@/components/Search/Suggestions/Suggestions.vue'
import { stickyHeader } from '@/Utils/directives.js'
import * as Sentry from '@sentry/vue'
import BannerAppPromotion from '@/components/Advertising/BannerAppPromotion.vue'

export default {
    name: 'Search',
    directives: { stickyHeader },
    components: { SearchResults, SearchBar, SearchFilters, Suggestions, BannerAppPromotion },
    props: {
        shouldFilter: {
            type: Boolean,
            default: true
        },
        withMainTags: {
            type: Boolean,
            default: true
        },
        store: {
            type: String,
            default: 'files'
        },
        suggestion: {
            type: String,
            default: 'file'
        },
        suggestionsTheme: {
            type: String,
            default: 'slider'
        },
        feature: {
            type: String,
            default: null
        },
        showCategories: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            cancelTokenSource: null,
            searchFormBusy: false,
            screenType: 'phone',
            mainTag: null,
            mainTags: [],
            tags: [],
            selectedFilters: {},
            showFilters: false,
            searchForm: {
                text: this.$route.query.text ? this.$route.query.text : '',
                type: this.$route.query.type ? this.$route.query.type : 'all',
                page: this.$route.query.page && Number.isInteger(parseInt(this.$route.query.page)) ? parseInt(this.$route.query.page) : 1,
                main: this.$route.query.main ? this.$route.query.main : undefined,
                tags: this.$route.query.tags ? (Array.isArray(this.$route.query.tags) ? this.$route.query.tags : [this.$route.query.tags]) : [],
                range: this.$route.query.range ? (Array.isArray(this.$route.query.range) ? this.$route.query.range : [this.$route.query.range]) : [],
                searchToken: this.$route.query.searchToken ? this.$route.query.searchToken : undefined,
                'options[highlight_color]': this.$route.query['options[highlight_color]'] ?? undefined,
                'options[package_results_only]': this.$route.query['options[package_results_only]'] ?? undefined
            }
        }
    },
    computed: {
        suggestions () {
            return this.$store.state.files.suggestion
        },
        canSearch () {
            return this.searchForm.text?.trim()?.length >= 3 || this.searchForm.main
        },
        searchResults () {
            return this.$store.state.search.results
        }
    },
    watch: {
        mainTag (tag) {
            this.searchForm.main = tag ? tag.id : null
        },
        $route: {
            handler () {
                this.fromQueryParamsToSearchForm()
                this.startSearch()
            },
            deep: true
        }
    },
    mounted () {
        const width = window.innerWidth
        if (width < 1024) {
            this.screenType = 'phone'
        } else if (width < 1400) {
            this.screenType = 'tablet'
        } else {
            this.screenType = 'desktop'
        }

        this.startSearch()
    },
    methods: {
        search () {
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.initSearch()
        },
        initSearch () {
            // cancel previous requests if any
            if (this.cancelTokenSource !== null) {
                this.cancelTokenSource.cancel()
            }
            this.cancelTokenSource = this.$axios.CancelToken.source()
            this.showFilters = false
            window.scrollTo({ top: 0, behavior: 'smooth' })
            this.updateURL()
        },
        startSearch () {
            this.$store.commit('search/setResults', null)
            if (this.searchForm.text.trim().length) {
                this.$store.commit(this.store + '/setHistory', null)
            }
            this.searchFormBusy = true
            this.searchFiltersBusy = this.tags.length === 0
            this.$axios.$get(this.localePath('/v1/search'), {
                cancelToken: this.cancelTokenSource?.token ?? undefined,
                params: this.searchForm
            }).then((data) => {
                if (this.canSearch) {
                    this.$store.commit('search/setResults', data)
                    this.searchForm.searchToken = data.searchToken
                }
                if (this.withMainTags) {
                    this.mainTags = data.mainTags
                    this.mainTag = this.mainTags.find(tag => tag.id === this.searchForm.main)
                }
                this.tags = data.tags
                this.selectedFilters = data.selected
                // send event only if user searched
                if (this.mainTag || this.searchForm.tags || this.searchForm.text) {
                    this.$gtm.push({
                        event: 'search',
                        search_term: this.searchForm.text
                    })
                }
            }).catch((e) => {
                if (!this.$axios.isCancel(e)) {
                    Sentry.captureException(e)
                    this.$store.commit('search/setResults', this.$t('general error') + '.')
                    this.searchForm.searchToken = undefined
                }
            }).finally(() => {
                this.cancelTokenSource = null
                this.searchFormBusy = false
                this.$helpers.eventBus.$emit('scroll-behavior-wait-done')
            })
        },
        updateURL (reset = false) {
            if (!this.canSearch && !reset) {
                return
            }
            this.$router.push({
                query: {
                    text: this.searchForm.text.trim().length ? this.searchForm.text.trim() : undefined,
                    type: this.searchForm.type === 'verbatim' ? 'verbatim' : undefined,
                    page: this.searchForm.page,
                    searchToken: this.searchForm.searchToken,
                    main: this.searchForm.main,
                    tags: this.searchForm.tags,
                    range: this.searchForm.range,
                    'options[highlight_color]': this.searchForm['options[highlight_color]'],
                    'options[package_results_only]': this.searchForm['options[package_results_only]']
                }
            })
        },
        addFilter (value) {
            const key = typeof value === 'object' ? 'range' : 'tags'
            if (key === 'range') {
                const range = JSON.parse(JSON.stringify(this.searchForm.range ?? []))
                const index = range.findIndex(f => f.startsWith(value.key))
                // add to end of array if not found or replace if found
                this.searchForm.range = range.toSpliced(index !== -1 ? index : 99999, 1, `${value.key}:${value.value}`)
            }
            if (key === 'tags') {
                const index = this.searchForm.tags.findIndex(tag => tag === value)
                if (index !== -1) {
                    return
                }
                this.searchForm.tags = this.searchForm.tags.concat([value])
            }
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.initSearch()
        },
        removeFilter (value) {
            const key = typeof value === 'object' ? 'range' : 'tags'
            const index = key === 'range'
                ? this.searchForm.range.findIndex(f => f.startsWith(value.key))
                : this.searchForm.tags.indexOf(value)
            const val = index !== -1 ? this.searchForm[key].toSpliced(index, 1) : undefined
            if (val !== undefined) {
                this.$set(this.searchForm, key, val)
                this.searchForm.page = 1
                this.searchForm.searchToken = undefined
                this.initSearch()
            }
        },
        resetFilter (values) {
            this.searchForm.tags = this.searchForm.tags.filter(t => !values.includes(t))
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.initSearch()
        },
        applyFilters ({ tag, values }) {
            // copy current search tags
            const tags = JSON.parse(JSON.stringify(this.searchForm.tags))
            // find filter tags
            const filterTags = this.searchForm.tags.filter(t => values.includes(t))
            // first delete all existing filters for this key
            for (const filterTag of filterTags) {
                const index = tags.indexOf(filterTag)
                tags.splice(index, 1)
            }
            // prepare new filters
            // merge with all tags
            this.searchForm.tags = tags.concat(values)
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.initSearch()
        },
        applyOptions (options) {
            for (const key in options) {
                this.$set(this.searchForm, `options[${key}]`, options[key] ? options[key] : undefined)
            }
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.initSearch()
        },
        addMainTags (tag) {
            this.searchForm.tags = []
            this.tags = []
            this.mainTag = null
            this.searchForm.page = 1
            this.searchForm.searchToken = undefined
            this.mainTag = tag || null
            this.searchForm.main = tag ? tag.id : undefined
            this.initSearch()
            this.updateURL(true)
            this.$store.commit('search/setResults', null)
        },
        changeSearchText (text) {
            this.searchForm.text = text
        },
        changeSearchType (type) {
            this.searchForm.type = type
            this.search()
        },
        toggleFilters () {
            this.showFilters = !this.showFilters
            if (this.showFilters) {
                this.$refs.bottomSheet.open()
            } else {
                this.$refs.bottomSheet.close()
            }
        },
        clearFilters () {
            this.$refs.bottomSheet.close()
            this.searchForm.tags = []
            this.searchForm.range = []
            this.initSearch()
        },
        changePage (page) {
            this.searchForm.page = page
            this.initSearch()
        },
        fromQueryParamsToSearchForm () {
            this.searchForm.text = this.$route.query.text ? this.$route.query.text : ''
            this.searchForm.page = this.$route.query.page && Number.isInteger(parseInt(this.$route.query.page)) ? parseInt(this.$route.query.page) : 1
            this.searchForm.main = this.$route.query.main ? this.$route.query.main : undefined
            this.searchForm.type = this.$route.query.type ? this.$route.query.type : 'all'
            this.searchForm.tags = this.$route.query.tags ? (Array.isArray(this.$route.query.tags) ? this.$route.query.tags : [this.$route.query.tags]) : []
            this.searchForm.range = this.$route.query.range ? (Array.isArray(this.$route.query.range) ? this.$route.query.range : [this.$route.query.range]) : []
        }
    }
}
